import { createContext, useContext, useState } from 'react';
import { api } from '../../services/api';
import { toast } from 'react-toastify';
import CookiesService from '../../services/cookies';

const LancamentoPerdasLaboratorioContext = createContext();
export const useLancamentoPerdasLaboratorio = () => useContext(LancamentoPerdasLaboratorioContext);

export const LancamentoPerdasLaboratorioProvider = ({ children }) => {
    const userLogin = CookiesService.getUserLogin();

    const [showModal, setShowModal] = useState(false);
    const [showConfirmacao, setShowConfirmacao] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    
    const [pecas, setPecas] = useState([]);
    const [amostra, setAmostra] = useState({});
    const [todasPecasPedido, setTodasPecasPedido] = useState([]);
    const [pecasExtrasDisponiveis, setPecasExtrasDisponiveis] = useState([]);
    
    const [motivos, setMotivos] = useState([]);
    const [observacao, setObservacao] = useState('');
    
    const [reqMaterial, setReqMaterial] = useState('');
    const [errosLancamento, setErrosLancamento] = useState([]);

    const [callbackFinalizar, setCallbackFinalizar] = useState(null);

    // Estados específicos do laboratório
    const [multiplicadores, setMultiplicadores] = useState({});
    const [pecasExtras, setPecasExtras] = useState([]);

    // Estados do modo lote
    const [isBatchMode, setIsBatchMode] = useState(false);
    const [quantidadeBatch, setQuantidadeBatch] = useState('');
    const [motivoBatch, setMotivoBatch] = useState(-1);
    const [multiplicadorBatch, setMultiplicadorBatch] = useState(1);

    // Carregar motivos
    const carregarMotivos = async () => {
        try {
            setIsLoading(true);
            const response = await api.get('/mp/lancamento-perdas/motivos');
            const motivosData = response.data?.data || response.data || [];
            setMotivos(Array.isArray(motivosData) ? motivosData : []);
        } catch (error) {
            toast.error('Erro ao carregar motivos de lançamento de perdas');
            setMotivos([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Buscar todas as peças do pedido
    const buscarPecasPedido = async (pedido, material, corMaterial) => {
        try {
            // Aqui você pode implementar a busca real das peças
            // Por enquanto vamos usar as peças que vierem do amostra
            return todasPecasPedido;
        } catch (error) {
            console.error('Erro ao buscar peças do pedido:', error);
            return [];
        }
    };

    // Buscar peças extras do endpoint
    const buscarPecasExtras = async (material, corMaterial, nfEntrada, nomeCliFor) => {
        try {
            if (!material || !corMaterial || !nfEntrada || !nomeCliFor) {
                console.warn('Parâmetros incompletos para buscar peças extras');
                setPecasExtrasDisponiveis([]);
                return [];
            }

            const response = await api.get('/lab-monitorar/pecas-extras', {
                params: {
                    Material: material,
                    CorMaterial: corMaterial,
                    NFEntrada: nfEntrada,
                    NomeCliFor: nomeCliFor
                }
            });

            const pecasData = response.data?.data || response.data || [];
            const pecasFormatadas = Array.isArray(pecasData) ? pecasData.map(item => ({
                peca: item.Peca || item.peca,
                metroForn: item.metroForn || item.MetroForn || item.Qtde || null,
                relogio: item.relogio || item.Relogio || null,
                ...item
            })) : [];

            setPecasExtrasDisponiveis(pecasFormatadas);
            return pecasFormatadas;
        } catch (error) {
            console.error('Erro ao buscar peças extras:', error);
            toast.error('Erro ao buscar peças extras');
            setPecasExtrasDisponiveis([]);
            return [];
        }
    };

    // Abrir modal de lançamento de perdas
    const abrirModal = (dadosPecas, dadosAmostra, todasPecas, onFinalizar) => {
        // Filtrar peças com enviaRevisao = 1 ou usar mock
        let pecasAmostragem = [];
        
        if (Array.isArray(dadosPecas) && dadosPecas.length > 0) {
            pecasAmostragem = dadosPecas.filter(p => p.enviaRevisao === 1 || p.enviaRevisao === '1');
        }
        
        // Se não houver peças filtradas, usar dados mocados
        if (pecasAmostragem.length === 0) {
            pecasAmostragem = [
                { peca: 'P001', enviaRevisao: 1, metroForn: 50, relogio: 48, partida: 'UNICA' },
                { peca: 'P002', enviaRevisao: 1, metroForn: 45, relogio: 44, partida: 'UNICA' }
            ];
        }

        setPecas(Array.isArray(pecasAmostragem) ? pecasAmostragem : []);
        setAmostra(dadosAmostra || {});
        // todasPecasPedido deve conter TODAS as peças (sem filtro enviaRevisao) para o select de peças extras
        setTodasPecasPedido(Array.isArray(todasPecas) ? todasPecas : (Array.isArray(dadosPecas) ? dadosPecas : []));
        setCallbackFinalizar(() => onFinalizar);
        
        // Inicializar multiplicadores como 1 para cada peça
        const multInicial = {};
        pecasAmostragem.forEach((peca, index) => {
            multInicial[index] = 1;
        });
        setMultiplicadores(multInicial);
        
        // Buscar peças extras quando o modal for aberto
        if (dadosAmostra) {
            const material = dadosAmostra.Material || dadosAmostra.material;
            const corMaterial = dadosAmostra.CorMaterial || dadosAmostra.cormaterial;
            const nfEntrada = dadosAmostra.NFEntrada || dadosAmostra.nfentrada;
            const nomeCliFor = dadosAmostra.NomeCliFor || dadosAmostra.nomeCliFor;
            
            if (material && corMaterial && nfEntrada && nomeCliFor) {
                buscarPecasExtras(material, corMaterial, nfEntrada, nomeCliFor);
            }
        }
        
        setShowModal(true);
        carregarMotivos();
    };

    // Fechar modal
    const fecharModal = () => {
        setShowModal(false);
        setShowSuccess(false);
        setErrosLancamento([]);
        setCallbackFinalizar(null);
        resetarFormulario();
    };

    const resetarFormulario = () => {
        setObservacao('');
        setReqMaterial('');
        setMultiplicadores({});
        setPecasExtras([]);
        setQuantidadeBatch('');
        setMotivoBatch(-1);
        setMultiplicadorBatch(1);
        setIsBatchMode(false);
    };

    // Adicionar peça extra
    const adicionarPecaExtra = () => {
        const novaPecaExtra = {
            pecaSelecionada: null,
            qtdeLancar: '',
            motivoSelecionado: -1,
            multiplicador: 1
        };
        setPecasExtras([...pecasExtras, novaPecaExtra]);
    };

    // Remover peça extra
    const removerPecaExtra = (index) => {
        const novasPecasExtras = pecasExtras.filter((_, i) => i !== index);
        setPecasExtras(novasPecasExtras);
    };

    // Atualizar peça extra
    const atualizarPecaExtra = (index, campo, valor) => {
        const novasPecasExtras = [...pecasExtras];
        novasPecasExtras[index][campo] = valor;
        setPecasExtras(novasPecasExtras);
    };

    // Atualizar multiplicador
    const atualizarMultiplicador = (index, valor) => {
        setMultiplicadores({
            ...multiplicadores,
            [index]: parseInt(valor) || 1
        });
    };

    const validarFormulario = () => {
        if (pecas.length === 0 && pecasExtras.length === 0) {
            toast.warning('Não há peças para lançar perdas');
            return false;
        }

        // Validação específica do modo lote
        if (isBatchMode) {
            const qtdBatch = parseFloat(quantidadeBatch);

            // Permitir lançamento apenas de peças extras se quantidade batch não estiver preenchida
            if ((!quantidadeBatch || qtdBatch <= 0) && pecasExtras.length === 0) {
                toast.warning('Informe uma quantidade válida para o lançamento em lote ou adicione peças extras');
                return false;
            }

            // Se quantidade batch estiver preenchida, validar motivo e multiplicador
            if (quantidadeBatch && qtdBatch > 0) {
                if (parseInt(motivoBatch) === -1) {
                    toast.warning('Selecione um motivo para o lançamento em lote');
                    return false;
                }

                if (multiplicadorBatch < 1) {
                    toast.warning('O multiplicador deve ser no mínimo 1');
                    return false;
                }
            }

            // Validar peças extras (em modo lote, apenas verificar se a peça foi selecionada)
            for (let i = 0; i < pecasExtras.length; i++) {
                const pecaExtra = pecasExtras[i];
                
                if (!pecaExtra.pecaSelecionada) {
                    toast.warning(`Peça extra ${i + 1}: Selecione uma peça`);
                    return false;
                }
                
                // Em modo lote, quantidade e motivo vêm do lote, não precisa validar aqui
                // A validação do lote já foi feita acima
            }

            return true;
        }

        // Validar peças principais (modo individual)
        const pecasComQuantidade = pecas.filter((peca, index) => {
            const qtd = parseFloat(peca.qtdeLancar) || 0;
            return qtd > 0;
        });
        
        if (pecasComQuantidade.length === 0 && pecasExtras.length === 0) {
            toast.warning('Defina pelo menos uma quantidade de perda maior que zero');
            return false;
        }

        // Validar motivos das peças principais
        const pecasSemMotivo = pecasComQuantidade.filter(peca => {
            const motivoSelecionado = peca.motivoSelecionado;
            return !motivoSelecionado || parseInt(motivoSelecionado) === -1;
        });

        if (pecasSemMotivo.length > 0) {
            const pecasTexto = pecasSemMotivo.map(p => p.peca).join(', ');
            toast.warning(`As peças com quantidade de perda devem ter motivo selecionado: ${pecasTexto}`);
            return false;
        }

        // Validar peças extras
        for (let i = 0; i < pecasExtras.length; i++) {
            const pecaExtra = pecasExtras[i];
            
            if (!pecaExtra.pecaSelecionada) {
                toast.warning(`Peça extra ${i + 1}: Selecione uma peça`);
                return false;
            }
            
            if (!pecaExtra.qtdeLancar || parseFloat(pecaExtra.qtdeLancar) <= 0) {
                toast.warning(`Peça extra ${i + 1}: Informe uma quantidade válida`);
                return false;
            }
            
            if (!pecaExtra.motivoSelecionado || parseInt(pecaExtra.motivoSelecionado) === -1) {
                toast.warning(`Peça extra ${i + 1}: Selecione um motivo`);
                return false;
            }
        }

        return true;
    };

    const processarLancamentoPerdas = async () => {
        if (!validarFormulario()) {
            return;
        }

        try {
            setIsLoading(true);

            // Processar peças principais com multiplicador
            let pecasParaLancar = [];
            
            if (isBatchMode) {
                // Modo lote: aplicar mesma quantidade, motivo e multiplicador para todas as peças
                const qtdBatch = parseFloat(quantidadeBatch);
                const motivoId = parseInt(motivoBatch);
                const multBatch = multiplicadorBatch;

                // Só processar peças principais se quantidade batch estiver preenchida
                if (qtdBatch > 0 && motivoId !== -1) {
                    pecas.forEach((peca) => {
                        // Criar N registros baseado no multiplicador batch
                        for (let i = 0; i < multBatch; i++) {
                            pecasParaLancar.push({
                                ...peca,
                                qtdeLancar: qtdBatch,
                                motivoSelecionado: motivoId
                            });
                        }
                    });
                }
            } else {
                // Modo individual: processar cada peça individualmente
                pecas.forEach((peca, index) => {
                    const qtdeLancar = peca.qtdeLancar || 0;
                    const motivoSelecionado = peca.motivoSelecionado;
                    
                    if (qtdeLancar > 0 && motivoSelecionado && parseInt(motivoSelecionado) !== -1) {
                        const multiplicador = multiplicadores[index] || 1;
                        
                        // Criar N registros baseado no multiplicador
                        for (let i = 0; i < multiplicador; i++) {
                            pecasParaLancar.push({
                                ...peca,
                                qtdeLancar: qtdeLancar,
                                motivoSelecionado: motivoSelecionado
                            });
                        }
                    }
                });
            }

            // Processar peças extras com multiplicador (tanto no modo lote quanto individual)
            pecasExtras.forEach(pecaExtra => {
                if (pecaExtra.pecaSelecionada) {
                    let qtdeLancar, motivoSelecionado, multiplicador;
                    
                    if (isBatchMode) {
                        // Em modo lote, usar valores do lote
                        qtdeLancar = parseFloat(quantidadeBatch) || 0;
                        motivoSelecionado = parseInt(motivoBatch);
                        multiplicador = multiplicadorBatch || 1;
                    } else {
                        // Em modo individual, usar valores da peça extra
                        qtdeLancar = parseFloat(pecaExtra.qtdeLancar) || 0;
                        motivoSelecionado = parseInt(pecaExtra.motivoSelecionado);
                        multiplicador = pecaExtra.multiplicador || 1;
                    }
                    
                    // Só processar se tiver quantidade e motivo válidos
                    if (qtdeLancar > 0 && motivoSelecionado && motivoSelecionado !== -1) {
                        for (let i = 0; i < multiplicador; i++) {
                            pecasParaLancar.push({
                                peca: pecaExtra.pecaSelecionada.peca || pecaExtra.pecaSelecionada,
                                qtdeLancar: qtdeLancar,
                                motivoSelecionado: motivoSelecionado,
                                metroForn: pecaExtra.pecaSelecionada.metroForn || 0,
                                relogio: pecaExtra.pecaSelecionada.relogio || 0
                            });
                        }
                    }
                }
            });

            if (pecasParaLancar.length === 0) {
                toast.warning('Nenhuma peça válida para lançamento.');
                return;
            }

            // Calcular total
            const qtdeTotalMaterial = pecasParaLancar.reduce((total, peca) => {
                return total + (parseFloat(peca.qtdeLancar) || 0);
            }, 0);

            // Informar usuário sobre quantos registros serão criados
            const totalRegistros = pecasParaLancar.length;
            console.log(`Serão criados ${totalRegistros} registros de perda`);

            const lancamento = {
                usuario: userLogin,
                filial: "REVISAO MP SOMA RBX",
                emissao: new Date().toISOString(),
                responsavel: userLogin,
                destino: "QUALIDADE",
                obs: observacao || 'Lançamento de perdas via laboratório',
                requisitante: "CONTROLE DE QUALIDADE",
                origem: "Laboratorio",
                materiais: [{
                    material: amostra.Material || amostra.material || '',
                    cor_material: amostra.CorMaterial || amostra.cormaterial || '',
                    qtde: qtdeTotalMaterial,
                    custo: 0,
                    data_custo: new Date().toISOString(),
                    fator_conversao_unidade: 1,
                    rateio_centro_custo: "00000",
                    rateio_filial: "00000",
                    pecas: pecasParaLancar.map(peca => ({
                        peca: peca.peca,
                        partida: "UNICA",
                        qtde: (peca.qtdeLancar || 0).toString(),
                        idMotivo: parseInt(peca.motivoSelecionado)
                    }))
                }]
            };

            const response = await api.post('/mp/lancamento-perdas/processar', lancamento);
            
            setReqMaterial(response.data.req_material || '');
            setShowSuccess(true);
            toast.success(`Lançamento de perdas realizado com sucesso! Total de ${totalRegistros} registros criados.`);
            
            // Executar callback de finalização após sucesso
            if (callbackFinalizar) {
                setTimeout(() => {
                    callbackFinalizar();
                    setCallbackFinalizar(null);
                }, 2000);
            }

        } catch (error) {
            if (error.response?.status === 409) {
                const mensagemErro = error.response?.data?.message || 'Erro de conflito no lançamento de perdas';
                toast.error(mensagemErro);
            } else {
                const mensagemErro = error.response?.data?.message || 'Erro ao processar lançamento de perdas';
                toast.error(mensagemErro);
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Handlers
    const handleObservacaoChange = (e) => {
        setObservacao(e.target.value);
    };

    const handleQuantidadeBatchChange = (e) => {
        const value = e.target.value.replace(',', '.');
        if (/^\d*\.?\d*$/.test(value)) {
            setQuantidadeBatch(value);
        }
    };

    const handleMotivoBatchChange = (e) => {
        setMotivoBatch(e.target.value);
    };

    const handleMultiplicadorBatchChange = (e) => {
        const value = parseInt(e.target.value) || 1;
        setMultiplicadorBatch(value >= 1 ? value : 1);
    };

    const toggleBatchMode = () => {
        setIsBatchMode(!isBatchMode);
        setQuantidadeBatch('');
        setMotivoBatch(-1);
        setMultiplicadorBatch(1);
    };

    return (
        <LancamentoPerdasLaboratorioContext.Provider
            value={{
                showModal,
                setShowModal,
                showConfirmacao,
                setShowConfirmacao,
                isLoading,
                showSuccess,

                pecas,
                amostra,
                motivos,
                reqMaterial,
                errosLancamento,
                todasPecasPedido,
                pecasExtrasDisponiveis,
                
                observacao,
                multiplicadores,
                pecasExtras,
                
                isBatchMode,
                quantidadeBatch,
                motivoBatch,
                multiplicadorBatch,
                
                abrirModal,
                fecharModal,
                processarLancamentoPerdas,
                buscarPecasPedido,
                buscarPecasExtras,
                toggleBatchMode,
                
                adicionarPecaExtra,
                removerPecaExtra,
                atualizarPecaExtra,
                atualizarMultiplicador,
                
                handleObservacaoChange,
                handleQuantidadeBatchChange,
                handleMotivoBatchChange,
                handleMultiplicadorBatchChange,
            }}
        >
            {children}
        </LancamentoPerdasLaboratorioContext.Provider>
    );
};

